import { useMemo } from 'react';
import { Box, Paper, Typography, Grid } from '@mui/material';
import {
  DashboardOutlined,
  PeopleOutlined,
  AccessTimeOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  ErrorOutlined,
} from '@mui/icons-material';
import { DayOverview, WeekOverview } from '../../types/dashboard.types';
import { Employee } from '../../types/employee.types';
import { Shop } from '../../types/shop.types';
import { Schedule } from '../../types/schedule.types';
import { calculateDayOverview, calculateWeekOverview } from '../../utils/dashboardCalculations';
import { getWeekStart } from '../../utils/scheduleHelpers';
import StatCard from './StatCard';

type ViewMode = 'daily' | 'weekly';

interface TodayOverviewCardProps {
  rawData: {
    employees: Employee[];
    shops: Shop[];
    schedules: Schedule[];
  };
  viewMode: ViewMode;
  selectedDate: string;
}

export default function TodayOverviewCard({
  rawData,
  viewMode,
  selectedDate,
}: TodayOverviewCardProps) {
  // Calculate data based on view mode
  const dayData: DayOverview | null = useMemo(() => {
    if (viewMode !== 'daily') return null;
    return calculateDayOverview(rawData.employees, rawData.shops, rawData.schedules, selectedDate);
  }, [rawData, selectedDate, viewMode]);

  const weekData: WeekOverview | null = useMemo(() => {
    if (viewMode !== 'weekly') return null;
    const weekStart = getWeekStart(new Date(selectedDate + 'T00:00:00'));
    return calculateWeekOverview(rawData.employees, rawData.shops, rawData.schedules, weekStart);
  }, [rawData, selectedDate, viewMode]);

  // Get status icon and color
  const coverageStatus = viewMode === 'daily' ? dayData?.coverageStatus : weekData?.coverageStatus;

  const getStatusIcon = () => {
    if (!coverageStatus) return <CheckCircleOutlined />;
    switch (coverageStatus.status) {
      case 'good':
        return <CheckCircleOutlined />;
      case 'warning':
        return <WarningAmberOutlined />;
      case 'critical':
        return <ErrorOutlined />;
    }
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' => {
    if (!coverageStatus) return 'success';
    switch (coverageStatus.status) {
      case 'good':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
    }
  };

  // Get total coverage gaps count
  const totalGaps = viewMode === 'daily'
    ? (dayData?.coverageGaps.length || 0)
    : (weekData?.totalCoverageGaps || 0);

  const title = viewMode === 'daily' ? 'Day Overview' : 'Week Overview';

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DashboardOutlined color="primary" />
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            value={viewMode === 'daily'
              ? `${dayData?.employeesWorking || 0} / ${dayData?.totalEmployees || 0}`
              : `${weekData?.employeesScheduled || 0} / ${weekData?.totalEmployees || 0}`
            }
            label={viewMode === 'daily' ? 'Employees Working' : 'Employees Scheduled'}
            icon={<PeopleOutlined />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            value={`${coverageStatus?.percentage || 100}%`}
            label="Coverage Status"
            icon={getStatusIcon()}
            color={getStatusColor()}
            subtitle={`${coverageStatus?.filled || 0} / ${coverageStatus?.required || 0} filled`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            value={`${viewMode === 'daily' ? (dayData?.totalScheduledHours || 0) : (weekData?.totalScheduledHours || 0)}h`}
            label="Total Scheduled"
            icon={<AccessTimeOutlined />}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            value={totalGaps.toString()}
            label="Coverage Gaps"
            icon={totalGaps === 0 ? <CheckCircleOutlined /> : <ErrorOutlined />}
            color={totalGaps === 0 ? 'success' : 'error'}
            subtitle={totalGaps === 0 ? 'All shifts covered' : `${totalGaps} need staff`}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
